import { Injectable } from '@angular/core';
import { EngagementAnalysis } from '@ex/module/core/data-models/engagement-analysis';
import { BehaviorSubject, Observable, Subject, combineLatest, interval, of } from 'rxjs';
import { EssentialObject, EssentialObjectService } from '@ex/module/core/services/essential-object.service';
import { bufferWhen, debounce, debounceTime, distinctUntilChanged, filter, map, pairwise, startWith, switchMap, take, tap, throttleTime, withLatestFrom } from 'rxjs/operators';
import { GeneralService } from '@ex/module/core/services/general.service';
import { FilterCountDataDto, FilterValue, GlobalCriteria, GlobalFilterSection } from '../models/models';
import { Hierarchy } from '@ex/module/core/data-models/hierarchy';
import { HierarchyEntity } from '@ex/module/core/data-models/hierarcy-entity';



@Injectable({
  providedIn: 'root'
})
export class GlobalFiltersHelperService {
  private essentialObject: EssentialObject;
  filters$: BehaviorSubject<GlobalFilterSection[]> = new BehaviorSubject([] as GlobalFilterSection[]);
  changedKey$: BehaviorSubject<string> = new BehaviorSubject('');
  changingKey$ = this.changedKey$.asObservable().pipe(
    distinctUntilChanged(), // optional: only emit if changed
    throttleTime(2000)      // emit first, then wait 2s before next
  );

  private treeGroupByOrder: { key: string, order: number }[] = [];
  private highestOrder!: { key: string, order: number };
  private stringifiedFilters: string = '[]';
  private counts: FilterCountDataDto[] | any = [];
  requestedFilters$: BehaviorSubject<FilterValue[]> = new BehaviorSubject([]);

  private criteriaKeys: string[];
  count: number = 3
  applyFilters$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  treeList$: BehaviorSubject<Hierarchy> = new BehaviorSubject({} as Hierarchy);
  selectedTreeEntities$: BehaviorSubject<Hierarchy[]> = new BehaviorSubject([]);
  selectedEntities: Observable<{ filters: FilterValue[], nodes: HierarchyEntity[], groupFilters: string[] }> = this.essentialObjectService.essentialObject.pipe(filter((x: EssentialObject) => !!x.selectedEntity), switchMap(obj => {
    const groupFilters = obj.selectedEntity.EntityGroups.filter(x => x?.parentKey).map(x => x.groupKey);

    return this.selectedTreeEntities$.asObservable().pipe(map(treeEntity => {
      let filters = [];
      let nodes: HierarchyEntity[] = [];
      treeEntity.forEach(tree => {
        this.applyValue(tree, filters, nodes);
      });

      const availableNodes = this.treeGroupByOrder.filter(x => treeEntity.find(y => y.entity.groupKey == x.key && y.checked))
      if (availableNodes.length)
        this.highestOrder = availableNodes?.reduce((prev, current) =>
          (prev.order < current.order ? prev : current)
        );
      else this.highestOrder = {} as any;
      console.log('this.highestOrder:', this.highestOrder);

      return {
        filters, nodes, groupFilters
      }
    }));
  }))
  constructor(private essentialObjectService: EssentialObjectService, private generalService: GeneralService) {

  }


  extractLegacyFilters(analysis: EngagementAnalysis, treeList: Hierarchy, count: number = 3): Observable<GlobalFilterSection[]> {
    this.count = count;
    if (this.filters$.value?.length && this.treeList$.value?.entity)
      return of(this.filters$.value);
    if (!this.treeList$.value?.entity)
      this.treeList$.next(deepCopy(treeList));
    const data = analysis.responsesCharts.map(chart => ({
      key: chart.key,
      name: chart.name,
      options: chart.options,
    } as GlobalCriteria));

    return this.populateFilters(data);

  }

  extractFilters(criterias, treeList: Hierarchy, count: number = 3): Observable<GlobalFilterSection[]> {
    this.count = count;

    if (this.filters$.value?.length && this.treeList$.value?.entity)
      return of(this.filters$.value);
    if (!this.treeList$.value?.entity)
      this.treeList$.next(deepCopy(treeList));
    return this.populateFilters(criterias);
  }
  populateFilters(criteria: any): Observable<GlobalFilterSection[]> {
    this.counts = [];
    const customCriteria = criteria.filter(x => !this.generalService.extraCriterias.find(e => e.key == x.key))
    this.criteriaKeys = [...new Set(criteria.map(x => x.key))] as string[];
    const filters = JSON.parse(JSON.stringify([
      {
        key: 'BusinessLevel',
        name: 'BusinessLevel',
        criteria: []
      },
      {
        key: 'Metrics',
        name: 'ExtraCriteria',
        criteria: [...this.generalService.extraCriterias, ...customCriteria.filter(f => f.isCustom)]
      },
      {
        key: 'Demographics',
        name: 'Demographics',
        criteria: criteria.filter(c => c.key.toLowerCase().includes('nationality') || c.key.toLowerCase().includes('gender') || c.key.toLowerCase().includes('generation') || c.key.toLowerCase().includes('agegroup'))
      },
      {
        key: 'HRMetrics',
        name: 'HR Metrics',
        criteria: criteria.filter(c => c.key.toLowerCase().includes('tenuregroup') || c.key.toLowerCase().includes('performance') || c.key.toLowerCase().includes('bandlevel') || c.key.toLowerCase().includes('jobgrade'))
      },

    ]));
    this.filters$.next(filters)
    return of(filters);
  }


  resetAllFilters() {
    this.removeFilters();
    this.resetTree();
    this.counts = [];
    this.applyFilters$.next(true);
    setTimeout(() => {
      this.applyFilters$.next(false);
    }, 1000);
  }

  resetTree() {
    this.selectedTreeEntities$.next([]);
    let currentTree = this.treeList$.value;
    this.changeChildrenStatus(currentTree, false)
    this.treeList$.next(currentTree);
  }

  combinedFilters(): Observable<{ filters: FilterValue[], keys: string[] }> {
    const formFilters$ = this.filters$.pipe(map(filterSections => {
      const filters = filterSections.filter(x => x.criteria.length).flatMap(x => x.criteria.filter(c => c.value?.length));
      return filters.map(filter => {
        return {
          key: filter.key, value: "IN", placeholderValue: (filter.value as string[]).map(val => `'${val}'`).join()
        }
      })
    }));

    return combineLatest([formFilters$, this.selectedEntities]).pipe(map(([formFilters, entityFilters]) => {
      const combinedFilters = [...formFilters, ...entityFilters.filters] as FilterValue[];
      return {
        filters: combinedFilters,
        keys: [...formFilters.map(x => x.key), ...entityFilters.groupFilters]
      }
    }))
  }

  getFilterCounts(essentialObject: EssentialObject, SurveyKey: string, SurveyType: string, service: any, module: boolean = false): Observable<any> {
    this.essentialObject = essentialObject;
    return this.changingKey$.pipe(switchMap(key => {
      console.log('key:', key);

      return this.combinedFilters().pipe(switchMap(({ filters, keys }) => {
        const oldFilters = JSON.parse(this.stringifiedFilters) as FilterValue[];
        let changedKey = filters.find(filter => JSON.stringify(oldFilters.find(f => f.key == filter.key)?.placeholderValue) !== JSON.stringify(filter.placeholderValue))?.key;

        if (changedKey) {
          this.changedKey$.next(changedKey);
        }
        changedKey = key ?? changedKey;

        this.stringifiedFilters = JSON.stringify(filters);

        const requestFilters = [...filters];

        this.requestedFilters$.next(requestFilters);
        if (this.counts.length)
          return of(requestFilters);

        
        const request = SurveyType ? {
          TenantKey: essentialObject.tenant.key,
          CompanyKey: essentialObject.selectedEntity.key,
          SurveyKey,
          SurveyType,
          FilterGroupKeys: this.criteriaKeys.length ? this.criteriaKeys : keys,
          Filters: requestFilters
        } : {
          tenantKey: essentialObject.tenant.key,
          companyKey: essentialObject.selectedEntity.key,
          surveyKey: SurveyKey,
          filterGroupKeys: module ? [...this.criteriaKeys,...keys] : keys,
          filters: requestFilters
        };

        if (!service) {
          this.assignCount(this.treeList$.value, this.counts as any);
          return of(request)
        }

        return service.getFilterCounts(request).pipe(
          map((data: any) => {

            this.counts = data?.Data || data?.data;
            if (!this.selectedTreeEntities$.value.find(x => x.entity.groupKey == changedKey)) {
              let currentTree = this.treeList$.value;
              this.assignCount(currentTree, this.counts as any);
              this.treeList$.next(currentTree);
            }
            let existingFilters = this.filters$.value;
            for (const group of existingFilters) {
              for (const criteria of group.criteria) {
                if (criteria.key === changedKey && criteria.value?.length && requestFilters?.length) {
                  continue;
                }

                criteria['count'] = 0;

                const criteriaCounts =
                  this.counts?.find(count => count.key === criteria.key)?.counts ??
                  this.counts?.find(count => count.Key === criteria.key)?.Counts;

                for (const option of criteria.options) {
                  option.count =
                    criteriaCounts?.find(count => count.key === option.key)?.count ??
                    criteriaCounts?.find(count => count.Key === option.key)?.Count;

                  if (!!option?.count) {
                    criteria['count'] += option.count;
                    if (option.count < this.count && criteria.value?.includes(option.key)) {
                      criteria.value.splice(criteria.value.indexOf(option.key), 1);
                      this.filters$.next(existingFilters);
                    }
                  }
                }

                criteria['validOptions'] = criteria.options.filter(option => option.count);
              }
            }
            return requestFilters;

          })
        );

      }))
    }))



  }




  private applyValue(row: Hierarchy, filters: any[], nodes: any[]) {
    if (!row.checked)
      return;

    const groupExists = filters?.find(f => f?.key == row.entity?.groupKey);
    if (groupExists) {
      filters[filters.indexOf(groupExists)].placeholderValue += (",'" + row.entity.key + "'");
    }
    else
      filters.push({ key: row.entity.groupKey, value: 'IN', placeholderValue: "'" + row.entity.key + "'", isEntity: true })

    nodes.push(row.entity);

    if (!row.children.length) {
      return;
    }
    row.children.forEach(childRow => {
      this.applyValue(childRow, filters, nodes)
    });
  }


  private assignCount(row: Hierarchy, counts: any[], parentRow?: Hierarchy) {
    if (!this.treeGroupByOrder.find(x => x.key == row.entity.groupKey))
      this.treeGroupByOrder.push({ key: row.entity.groupKey, order: this.treeGroupByOrder.length });





    let groupKeyFound = counts.find(count => count?.Key == row.entity.groupKey || count?.key == row.entity.groupKey);
    if (groupKeyFound && !!groupKeyFound['counts'])
      groupKeyFound.Counts = groupKeyFound['counts'];
    let foundCount = groupKeyFound?.Counts.find(c => row.entity.key == c?.key || row.entity.key == c?.Key);
    if (foundCount && !!foundCount['count'])
      foundCount.Count = foundCount['count'];
    let count = foundCount?.Count ?? foundCount?.count ?? 0;
    if (!count && parentRow?.checked)
      count = row.entity.count;
    row.entity.count = count;
    if (count < this.count && row.checked) {
      this.handleTreeClick(row)
    }

    if (!row?.children?.length)
      return
    row.children.forEach(childRow => {
      this.assignCount(childRow, counts, row);
    });
  }


  handleTreeClick(childTree: Hierarchy, checked = !childTree.checked, update: boolean = true) {
    const treeOrder = this.treeGroupByOrder?.find(x => x?.key == childTree?.entity?.groupKey);
    if (treeOrder?.order < this.highestOrder?.order && checked)
      this.checkHigherTrees(treeOrder);
    else if (treeOrder?.order > this.highestOrder?.order && checked)
      this.getParentTree(childTree.entity.parentKey, this.highestOrder.key);
    this.entityToggle(childTree, checked, update);
  }
  checkHigherTrees(treeOrder: { key: string, order: number }) {
    this.selectedTreeEntities$.value.forEach(tree => {
      const order = this.treeGroupByOrder?.find(x => x?.key == tree.entity?.groupKey && x?.order > treeOrder.order);
      if (order)
        this.getParentTree(tree.entity.parentKey, treeOrder.key);
    })
  }



  getParentTree(startingParentKey: string, selectedGroupKey: string) {
    const treeList = this.treeList$.value;

    // 1. Flatten the entire hierarchy into a flat list
    const allEntities = this.flattenTreeList(treeList);

    // 2. Start from the parentKey and walk upward
    let currentKey = startingParentKey;

    while (currentKey) {
      const parentNode = allEntities.find(node => node.entity.key === currentKey);

      if (!parentNode) break;

      if (parentNode.entity.groupKey === selectedGroupKey) {
        this.entityToggle(parentNode, true, true);
        break;
      }

      currentKey = parentNode.entity.parentKey;
    }
  }

  // Helper to flatten the tree into a list of nodes
  private flattenTreeList(tree: Hierarchy | Hierarchy[]): Hierarchy[] {
    const result: Hierarchy[] = [];
    const stack: Hierarchy[] = Array.isArray(tree) ? [...tree] : [tree];

    while (stack.length > 0) {
      const node = stack.pop();
      if (!node) continue;

      result.push(node);

      if (node.children?.length) {
        stack.push(...node.children);
      }
    }

    return result;
  }




  private entityToggle(childTree: Hierarchy, checked: boolean, update: boolean) {
    let treeList = this.treeList$.value;
    this.findChildTree(treeList, childTree, checked)
    console.log(treeList);
    if (update)
      this.treeList$.next(treeList);
  }

  private findChildTree(tree: Hierarchy, childTree: Hierarchy, checked: boolean) {
    if (childTree.entity.key == tree.entity.key) {
      let currentEntites = this.selectedTreeEntities$.value;
      const ind = currentEntites.find(x => x.entity.key == childTree.entity.key)
      if (!checked && ind) {
        currentEntites.splice(currentEntites.indexOf(ind), 1)
      }
      else {
        currentEntites.push(childTree);
      }
      this.changeChildrenStatus(childTree, checked)
      this.selectedTreeEntities$.next(currentEntites);
      return;
    }
    if (!tree?.children?.length)
      return;
    tree.children.forEach(childRow => {
      this.findChildTree(childRow, childTree, checked)
    });

  }

  private changeChildrenStatus(childTree: Hierarchy, checked: boolean) {
    childTree.checked = checked
    if (!childTree?.children?.length)
      return;
    childTree.children.forEach(childRow => {
      this.changeChildrenStatus(childRow, checked)
    });
  }



  resetEntityGroup(groupKey: string) {
    let treeList = this.treeList$.value;
    this.removeChildTreeByGroupKey(treeList, groupKey)
    console.log(treeList);
    this.treeList$.next(treeList);
  }


  private removeChildTreeByGroupKey(tree: Hierarchy, groupKey: string) {
    if (tree.entity.groupKey == groupKey) {
      this.handleTreeClick(tree, false, false)
      return
    }
    if (!tree?.children?.length)
      return;
    tree.children.forEach(childRow => {
      this.removeChildTreeByGroupKey(childRow, groupKey)
    });

  }

  removeFilters(key?: string) {
    let filters = this.filters$.value;
    filters.forEach(filter => {
      filter.criteria.forEach(f => {
        if (f.key == key || !key)
          f.value = []
      })
    })
    this.filters$.next(filters)
  }


  clearFilters() {
    this.removeFilters();
    setTimeout(() => {
      this.selectedTreeEntities$.next([]);
      this.filters$.next([]);
      this.treeList$.next({} as any);
    }, 2000);
  }



  generateBarFilters(): Observable<{ entities: HierarchyEntity[], filters: GlobalCriteria[] }> {
    return this.filters$.pipe(debounceTime(200), switchMap(filters => {
      const filtersCopy = structuredClone(filters);
      return this.applyFilters$.pipe(filter(x => x), map(_ => {
        return filtersCopy.filter(x => x.criteria.length).flatMap(x => x.criteria.filter(c => c.value?.length));
      }));
    })).pipe(switchMap(filters => {
      return this.selectedEntities.pipe(debounceTime(200), switchMap(({ nodes }) => {
        return this.applyFilters$.pipe(filter(x => x), map(_ => {
          nodes = nodes.reduce((acc, curr) => {
            if (!acc?.length)
              acc = [];
            const groupExists = acc.find(k => k.key == curr.groupKey)
            if (!groupExists) {
              const name = (this.essentialObject.selectedEntity.EntityGroups.find(group => group.groupKey == curr.groupKey)??curr)?.name;
              acc.push({ key: curr.groupKey, name, nodes: [curr] })
              return acc;
            }
            acc[acc.indexOf(groupExists)].nodes.push(curr)
            return acc;
          }, [])
          console.log('nodes:', nodes);

          return nodes;
        }))
      })).pipe(map(entities => ({ entities, filters })))

    }));
  }

  parseLegacyFilters(filters: any, chartFilters: any[]) {
    if (filters && filters.length == 0) {
      return [];
    }

    // Flatten and normalize filters
    filters = filters.flatMap((filter: any) => {
      if (!filter.hasOwnProperty('placeholderValue')) return filter;

      const uniqueValues = Array.from(
        new Set(
          filter.placeholderValue
            .replace(/'/g, "")
            .split(",")
            .map((v: string) => v.trim())
        )
      );

      return uniqueValues.map(value => ({
        key: filter.key,
        value
      }));
    });


    // Group filters by key
    const groupedFilters = filters.reduce((acc, current) => {
      const existing = acc.find(item => item.key === current.key);

      if (existing) {
        existing.value.push(current.value);
      } else {
        acc.push({
          key: current.key,
          value: Array.isArray(current.value) ? current.value : [current.value]
        });
      }
      return acc;
    }, [] as { key: string; value: any[] }[]);

    // Merge into chartFilters
    groupedFilters.forEach(filter => {
      const index = chartFilters?.findIndex(x => x.key === filter.key);

      if (index === -1 || index === undefined) {
        chartFilters.push(filter);
      } else {
        chartFilters[index] = filter;
      }
    });

    return chartFilters;
  }

}

function deepCopy(obj) {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }
  const copy = Array.isArray(obj) ? [] : {};
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      copy[key] = deepCopy(obj[key]);
    }
  }
  return copy;
}